import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'

export interface ApiKey {
  id: string
  name: string
  key: string
  permissions: string
  created: string
  lastUsed: string
  callsToday: number
  rateLimit: number
}

export interface UserProfile {
  firstName: string
  lastName: string
  email: string
  timezone: string
  units: 'metric' | 'imperial'
  responseStyle: 'concise' | 'detailed' | 'tables-only'
  defaultRegion: string
  defaultModels: string[]
  athenaPresets: Record<string, boolean>

  // Data & Display
  defaultChartTimeframe: '6h' | '12h' | '24h' | '48h' | '7d'
  defaultMapLayer: 'wind' | 'solar' | 'temperature' | 'pressure'
  pricePrecision: number
  powerPrecision: number
  priceCurrency: string
  capacityDisplay: 'absolute' | 'percentage'
  priceDisplay: 'mwh' | 'kwh'
  defaultBiddingZone: string
  tradingCalendars: string[]

  // Notifications
  notifChannels: {
    inApp: boolean
    push: boolean
    email: boolean
    slackWebhook: string
  }
  quietHoursEnabled: boolean
  quietHoursStart: string
  quietHoursEnd: string
  severityThreshold: 'all' | 'warning_critical' | 'critical'
  digestFrequency: 'realtime' | 'hourly' | 'daily'

  // API Keys
  apiKeys: ApiKey[]

  // Onboarding
  hasCompletedOnboarding: boolean
  role: 'energy_trader' | 'meteorologist' | 'analyst' | 'portfolio_manager' | ''
  company: string
  secondaryZones: string[]
}

const DEFAULT_PROFILE: UserProfile = {
  firstName: 'Mathias',
  lastName: 'Vanderbrempt',
  email: 'mathiasvanderbrempt@gmail.com',
  timezone: 'Europe/Berlin',
  units: 'metric',
  responseStyle: 'concise',
  defaultRegion: 'central-europe',
  defaultModels: ['EPT-2', 'EC AIFS', 'EC IFS'],
  athenaPresets: {
    tables: true,
    deltas: true,
    divergence: true,
    wind: true,
    spreads: true,
    flows: false,
    balancing: false,
    confidence: true,
  },
  defaultChartTimeframe: '24h',
  defaultMapLayer: 'wind',
  pricePrecision: 2,
  powerPrecision: 1,
  priceCurrency: 'EUR',
  capacityDisplay: 'absolute',
  priceDisplay: 'mwh',
  defaultBiddingZone: 'DE-LU',
  tradingCalendars: ['EPEX', 'Nord Pool'],
  notifChannels: {
    inApp: true,
    push: false,
    email: true,
    slackWebhook: '',
  },
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '06:00',
  severityThreshold: 'all',
  digestFrequency: 'realtime',
  apiKeys: [
    {
      id: 'key-1',
      name: 'Production Trading Algo',
      key: 'jua_live_8f92...3b1a',
      permissions: 'Read-only',
      created: '2025-11-12',
      lastUsed: '2 mins ago',
      callsToday: 8450,
      rateLimit: 10000,
    },
  ],
  hasCompletedOnboarding: false,
  role: '',
  company: '',
  secondaryZones: [],
}

interface UserProfileContextValue {
  profile: UserProfile
  savedProfile: UserProfile
  isDirty: boolean
  updateProfile: (updates: Partial<UserProfile>) => void
  saveProfile: () => void
  resetProfile: () => void
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null)

function loadProfile(): UserProfile {
  try {
    const stored = localStorage.getItem('jua-user-profile')
    if (stored) {
      return { ...DEFAULT_PROFILE, ...JSON.parse(stored) }
    }
  } catch (e) {
    console.error('Failed to load user profile', e)
  }
  return DEFAULT_PROFILE
}

export function UserProfileProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [savedProfile, setSavedProfile] = useState<UserProfile>(loadProfile)
  const [profile, setProfile] = useState<UserProfile>(savedProfile)

  const isDirty = useMemo(() => {
    return JSON.stringify(profile) !== JSON.stringify(savedProfile)
  }, [profile, savedProfile])

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile((prev) => ({ ...prev, ...updates }))
  }, [])

  const saveProfile = useCallback(() => {
    setSavedProfile(profile)
    localStorage.setItem('jua-user-profile', JSON.stringify(profile))
  }, [profile])

  const resetProfile = useCallback(() => {
    setProfile(savedProfile)
  }, [savedProfile])

  return React.createElement(
    UserProfileContext.Provider,
    {
      value: {
        profile,
        savedProfile,
        isDirty,
        updateProfile,
        saveProfile,
        resetProfile,
      },
    },
    children,
  )
}

export function useUserProfile(): UserProfileContextValue {
  const ctx = useContext(UserProfileContext)
  if (!ctx)
    throw new Error('useUserProfile must be used within UserProfileProvider')
  return ctx
}
